import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  InputMaybe,
  InsertDocumentAssessmentResultMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface InsertDocumentAssessmentResultTRPCInput {
  Rating: number;
  DocumentIds: string[];
  AssessmentId?: InputMaybe<string>;
  TestDate?: InputMaybe<string>;
  Rationale?: InputMaybe<string>;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
}

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Ids: string[];
}): InsertDocumentAssessmentResultMutation => {
  return {
    insertChildDocumentAssessmentResult: {
      __typename: 'InsertChildDocumentAssessmentResultOutput',
      Ids: trpcData.Ids,
    },
  };
};

export const useInsertDocumentAssessmentResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.documentAssessmentResult.insert.mutationOptions({
      onSuccess: async () => {
        // Invalidation parity with the legacy Apollo evictFields:
        // document_assessment_result (+ aggregate), assessment,
        // internal_audit_report and oversight_assessment.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.documentAssessmentResult.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.assessment.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.internalAuditReport.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.oversight.pathKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertDocumentAssessmentResult: async (
      variables: InsertDocumentAssessmentResultTRPCInput
    ): Promise<InsertDocumentAssessmentResultMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
